import { useLocalStorage } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';
import { CashflowFrequency, Period } from '../utils';
import { Dispatch, SetStateAction } from 'react';

export type IncomeType =
  | 'Income'
  | 'Benefit'
  | 'Employment'
  | 'ShareOrDividend'
  | 'Rental'
  | 'Pension';

export type Income = {
  id: string;
  /** Shared by the one-row-per-member records of an item entered once for several members. */
  groupId?: string | null;
  description: string | null;
  frequency: CashflowFrequency | null;
  amountPreTax: number | null;
  amountPostTax: number | null;
  incomeType: IncomeType;
  clientId: string;
};

export type EditableIncome = Omit<Income, 'id'> & {
  id?: string;
};

type UseIncomesReturnType = {
  isLoading: boolean;
  incomesData: Income[] | undefined;
  upsertIncomes: (items: EditableIncome[]) => Promise<void>;
  deleteIncomes: (ids: string[]) => Promise<void>;
  period: Period;
  setPeriod: Dispatch<SetStateAction<Period>>;
};

export default function useIncomes(): UseIncomesReturnType {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useLocalStorage<Period>({
    key: 'cashflow-incomes-frequency',
    defaultValue: 'monthly',
  });

  const queryKey = ['clients', clientId, 'incomes'];

  const { data: incomesData, isLoading } = useQuery<Income[]>({
    queryKey,
  });

  const { mutateAsync: upsertMutation } = useMutation({
    mutationFn: (items: EditableIncome[]) =>
      api.put(
        `clients/${clientId}/incomes/batch`,
        items.map(item => ({ ...item, clientId })),
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const upsertIncomes = async (items: EditableIncome[]) => {
    await upsertMutation(items);
  };

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (ids: string[]) => api.post(`clients/${clientId}/incomes/delete-request`, ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteIncomes = async (ids: string[]) => {
    await deleteMutation(ids);
  };

  return {
    isLoading,
    incomesData,
    upsertIncomes,
    deleteIncomes,
    period,
    setPeriod,
  };
}
